import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import type { Client } from "@/api/schemas";
import { filtrarClientes, normalizar, ordenarParaSelector } from "@/utils/clientes";
import { ClientBrand } from "./ClientBrand";
import styles from "./ClientPicker.module.css";

export interface PickerCommand {
  id: string;
  label: string;
  onSelect: () => void;
}

export interface PickerCommandGroup {
  id: string;
  title: string;
  commands: PickerCommand[];
}

interface ClientPickerProps {
  clients: readonly Client[];
  actualSlug: string | null;
  /** Slugs usados, el más reciente primero. */
  recientes: readonly string[];
  onSelect: (client: Client) => void;
  /** «Ir a» y «Acciones» de la paleta ⌘K: el mismo listbox, más grupos. */
  commandGroups?: PickerCommandGroup[];
  label?: string;
  placeholder?: string;
  noResultsText?: string;
  footer?: ReactNode;
}

type Option =
  | { kind: "client"; id: string; client: Client; current: boolean }
  | { kind: "command"; id: string; command: PickerCommand };

interface Group {
  id: string;
  title: string;
  options: Option[];
}

function buildGroups(
  uid: string,
  clients: readonly Client[],
  actualSlug: string | null,
  recientes: readonly string[],
  query: string,
  commandGroups: PickerCommandGroup[],
): Group[] {
  let counter = 0;
  const clientOption = (client: Client): Option => ({ kind: "client", id: `${uid}-opt-${counter++}`, client, current: client.slug === actualSlug });
  const groups: Group[] = [];
  if (query.trim()) {
    const found = filtrarClientes(clients, query);
    if (found.length) groups.push({ id: "clientes", title: "Clientes", options: found.map(clientOption) });
  } else {
    const lists = ordenarParaSelector(clients, { actualSlug, recientes });
    if (lists.recientes.length) groups.push({ id: "recientes", title: "Recientes", options: lists.recientes.map(clientOption) });
    if (lists.todos.length) groups.push({ id: "todos", title: "Todos", options: lists.todos.map(clientOption) });
  }
  const needle = normalizar(query.trim());
  for (const group of commandGroups) {
    const commands = group.commands.filter((command) => !needle || normalizar(command.label).includes(needle));
    if (commands.length) {
      groups.push({ id: group.id, title: group.title, options: commands.map((command): Option => ({ kind: "command", id: `${uid}-opt-${counter++}`, command })) });
    }
  }
  return groups;
}

/** Posición inicial: el cliente ANTERIOR, para que abrir y pulsar Enter alterne entre los dos últimos. */
function initialIndex(options: Option[], actualSlug: string | null, recientes: readonly string[]): number {
  const anterior = recientes.find((slug) => slug !== actualSlug && options.some((o) => o.kind === "client" && o.client.slug === slug));
  if (!anterior) return 0;
  const index = options.findIndex((o) => o.kind === "client" && o.client.slug === anterior);
  return index < 0 ? 0 : index;
}

/**
 * Combobox + listbox con `aria-activedescendant` (el patrón de la paleta): busca sin tildes ni
 * mayúsculas, agrupa en Recientes y Todos y resalta primero al cliente anterior. Un solo componente de
 * lista con dos envoltorios: el popover bajo el disparador y la paleta ⌘K.
 */
export function ClientPicker({
  clients,
  actualSlug,
  recientes,
  onSelect,
  commandGroups = [],
  label = "Buscar un cliente",
  placeholder = "Buscar cliente…",
  noResultsText = "Ningún cliente se llama así.",
  footer,
}: ClientPickerProps) {
  const uid = useId();
  const listboxId = `${uid}-listbox`;
  const [query, setQuery] = useState("");
  const groups = useMemo(() => buildGroups(uid, clients, actualSlug, recientes, query, commandGroups), [uid, clients, actualSlug, recientes, query, commandGroups]);
  const options = useMemo(() => groups.flatMap((g) => g.options), [groups]);
  const [activeIndex, setActiveIndex] = useState(() => initialIndex(options, actualSlug, recientes));
  const inputRef = useRef<HTMLInputElement>(null);
  const activeRef = useRef<HTMLDivElement>(null);
  const active = options[Math.min(activeIndex, options.length - 1)];

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    activeRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [active?.id]);

  function choose(option: Option | undefined) {
    if (!option) return;
    if (option.kind === "client") onSelect(option.client);
    else option.command.onSelect();
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex(Math.min(activeIndex + 1, options.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex(Math.max(activeIndex - 1, 0));
    } else if (event.key === "Home") {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setActiveIndex(Math.max(options.length - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      choose(active);
    }
  }

  return (
    <div className={styles.picker}>
      <input
        ref={inputRef}
        className={styles.input}
        type="text"
        role="combobox"
        aria-label={label}
        aria-autocomplete="list"
        aria-expanded="true"
        aria-controls={listboxId}
        aria-activedescendant={active?.id}
        autoComplete="off"
        spellCheck={false}
        placeholder={placeholder}
        value={query}
        onChange={(event) => {
          const value = event.target.value;
          setQuery(value);
          setActiveIndex(0);
        }}
        onKeyDown={onKeyDown}
      />
      <div id={listboxId} role="listbox" aria-label={label} className={styles.list}>
        {groups.map((group) => (
          <div key={group.id} role="group" aria-labelledby={`${uid}-${group.id}`}>
            <p id={`${uid}-${group.id}`} className={styles.groupTitle}>{group.title}</p>
            {group.options.map((option) => (
              <div
                key={option.id}
                id={option.id}
                ref={option.id === active?.id ? activeRef : undefined}
                role="option"
                aria-selected={option.id === active?.id}
                aria-current={option.kind === "client" && option.current ? "true" : undefined}
                className={`${styles.option} ${option.id === active?.id ? styles.optionActive : ""}`}
                onMouseDown={(event) => event.preventDefault()}
                onMouseMove={() => setActiveIndex(options.indexOf(option))}
                onClick={() => choose(option)}
              >
                {option.kind === "client" ? (
                  <>
                    <ClientBrand client={option.client} size={20} />
                    <span className={styles.name} title={option.client.name}>{option.client.name}</span>
                    {option.current ? <span className={styles.current}>Actual</span> : null}
                  </>
                ) : (
                  <span className={styles.name}>{option.command.label}</span>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
      {options.length === 0 ? <p className={styles.empty} role="status">{noResultsText}</p> : null}
      {footer ? <div className={styles.footer}>{footer}</div> : null}
    </div>
  );
}
